import { useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import type { Column, SortingRule } from 'react-table';

import useTeamGroups from '../../../../../hooks/api/teams/teamGroups/useTeamGroups';
import SimpleReactTable from '../../../../generic/SimpleReactTable';
import TableHeader from '../../../../generic/TableHeader';
import type { FullTeamGroupResponse } from '../../../../../../shared/types/routes/teamGroups';

import TeamGroupsTableRowActions from './TeamGroupsTableRowActions';

const defaultSortMode = [
  {
    id: 'name',
    desc: false,
  },
];

const useTeamGroupsColumns = (sortMode: SortingRule): Array<Column<FullTeamGroupResponse>> => useMemo(() => [
  {
    id: 'name',
    Header: <TableHeader title="Team Group Name" sort={{ id: 'name', currentMode: sortMode }} />,
    accessor: 'name',
    resizable: false,
    style: {
      textOverflow: 'ellipsis',
    },
  },
  {
    id: 'teamsCount',
    Header: <TableHeader title="Teams" sort={{ id: 'teamsCount', currentMode: sortMode }} />,
    accessor: (row: FullTeamGroupResponse) => row.teams.length,
    width: 250,
    resizable: false,
  },
  {
    id: 'membersCount',
    Header: <TableHeader title="Members" sort={{ id: 'membersCount', currentMode: sortMode }} />,
    accessor: (row: FullTeamGroupResponse) => row.members.length,
    width: 250,
    resizable: false,
  },
  {
    id: 'actions',
    Header: <TableHeader title="Actions" />,
    accessor: () => null,
    Cell: ({ original }: { original: FullTeamGroupResponse }) => (
      <TeamGroupsTableRowActions
        teamGroupId={original.id}
        teamGroupName={original.name}
      />
    ),
    width: 100,
    resizable: false,
    sortable: false,
  },
], [sortMode]);

/**
 * Filters the team groups based on the search term.
 * @param {FullTeamGroupResponse[] | undefined} teamGroups - The list of team groups to filter
 * @param {string} search - The search term
 * @returns {FullTeamGroupResponse[]} The filtered list of team groups
 */
const useFilteredTeamGroups = (teamGroups: FullTeamGroupResponse[] | undefined, search: string) => useMemo(() => {
  if (!teamGroups) {
    return [];
  }
  if (!search) {
    return teamGroups;
  }
  const lowerSearch = search.toLowerCase();
  return teamGroups.filter((teamGroup) => teamGroup.name.toLowerCase().includes(lowerSearch));
}, [teamGroups, search]);

type Props = {
  //  This is managed in the parent component (because it's used for both the team groups and teams tables).
  search: string;
};

/**
 * Displays the list of team groups
 */
const TeamGroupsTable: VoidFunctionComponent<Props> = ({ search }) => {
  const [sortMode, setSortMode] = useState<SortingRule>(defaultSortMode[0] as SortingRule);
  const { data: teamGroups, isLoading } = useTeamGroups();
  const filteredTeamGroups = useFilteredTeamGroups(teamGroups, search);
  const columns = useTeamGroupsColumns(sortMode);

  return (
    <SimpleReactTable
      data={filteredTeamGroups}
      columns={columns}
      defaultSortMode={defaultSortMode}
      onSortedChanged={setSortMode}
      showPagination={filteredTeamGroups.length > 20}
      cacheId="organizationTeamGroups"
      isLoading={isLoading}
    />
  );
};

export default TeamGroupsTable;
